import androidx.compose.runtime.*
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

/**
 * Only rounds with at least one played match make sense to browse here (nothing to show otherwise).
 * Nadstavba (spec 12): O titul and O udržení share the same round -- one entry, not two -- so
 * matches are canonicalized before grouping; a round counts as played if EITHER group has a
 * completed match in it.
 */
private fun playedRoundOptions(matches: List<MatchDto>): List<Pair<String, Int>> {
    val normalized = matches.map { it.copy(phase = canonicalPlayoffPhase(it.phase)) }
    return roundOptionsOf(normalized).filter { (phase, round) ->
        normalized.any { it.phase == phase && it.roundNumber == round && it.status == "completed" }
    }
}

/** The nominees with the highest totalScore in a round (the winner(s); ties are all winners). */
private fun winnersOf(nominees: List<RoundNomineeDto>): List<RoundNomineeDto> {
    val maxScore = nominees.maxOfOrNull { it.totalScore } ?: return emptyList()
    return nominees.filter { it.totalScore == maxScore }
}

/** One row's worth of data for the overview table; `leagueName` is only shown in the all-leagues variant. */
private data class RoundRowData(
    val key: String,
    val leagueName: String,
    val phase: String,
    val round: Int,
    val response: PlayerOfRoundResponseDto?,
)

/**
 * Shared overview table: one row per round showing its winner(s); a row expands on click to list
 * every nominee for that round (so ties, and players who qualified but didn't win, stay visible
 * without cluttering the overview). `showLeagueColumn` adds a "Liga" column for the all-leagues
 * variant, where rows from different leagues are interleaved by round.
 */
@Composable
private fun PlayerOfRoundTable(
    rows: List<RoundRowData>,
    showLeagueColumn: Boolean,
    expanded: Set<String>,
    onToggle: (String) -> Unit,
) {
    val colCount = if (showLeagueColumn) 6 else 5
    Table(attrs = { classes("public-table") }) {
        Tr {
            if (showLeagueColumn) Th { Text("Liga") }
            Th { Text("Kolo") }
            Th { Text("Vítěz kola") }
            Th(attrs = { classes("col-accent") }) { Text("Nához") }
            Th { Text("Nominovaní") }
            Th { Text("Stav") }
        }
        rows.forEach { row ->
            val nominees = row.response?.nominees.orEmpty()
            val winners = winnersOf(nominees)
            val isExpanded = row.key in expanded

            Tr(attrs = {
                classes("clickable-row")
                onClick { onToggle(row.key) }
            }) {
                if (showLeagueColumn) Td { Text(row.leagueName) }
                Td { Text((if (isExpanded) "▾ " else "▸ ") + roundSlotLabel(row.phase, row.round)) }
                Td { Text(if (winners.isEmpty()) "—" else winners.joinToString(", ") { it.playerName }) }
                Td { Span(attrs = { classes("pill") }) { Text(winners.firstOrNull()?.totalScore?.toString() ?: "—") } }
                Td { Text(if (nominees.isEmpty()) "—" else nominees.size.toString()) }
                Td {
                    row.response?.let {
                        Span(attrs = { classes("tag", if (it.isFinal) "final" else "provisional") }) {
                            Text(if (it.isFinal) "Finální" else "Průběžné")
                        }
                    }
                }
            }
            if (isExpanded) {
                Tr(attrs = { classes("round-detail-row") }) {
                    Td(attrs = { attr("colspan", colCount.toString()) }) {
                        if (nominees.isEmpty()) {
                            Text(
                                if (row.response?.isFinal == true) "V tomto kole nikdo nezískal všechny 3 duelové body."
                                else "V tomto kole zatím nikdo nezískal všechny 3 duelové body."
                            )
                        } else {
                            Table(attrs = { classes("nested-table") }) {
                                Tr {
                                    Th { Text("Hráč") }
                                    Th { Text("Tým") }
                                    Th { Text("Nához") }
                                }
                                nominees.sortedByDescending { it.totalScore }.forEach { n ->
                                    Tr(attrs = { if (winners.any { w -> w.playerId == n.playerId }) classes("winner-row") }) {
                                        Td { Text(n.playerName) }
                                        Td { Text(n.teamName) }
                                        Td { Text(n.totalScore.toString()) }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

/** Single-league view: round selector implied by the outer league tabs; `matches` is the league's already-fetched match list. */
@Composable
fun PlayerOfRoundSection(leagueId: String, matches: List<MatchDto>) {
    val roundOptions = remember(matches) { playedRoundOptions(matches) }
    var responses by remember(leagueId) { mutableStateOf<Map<String, PlayerOfRoundResponseDto>>(emptyMap()) }
    var expanded by remember(leagueId) { mutableStateOf<Set<String>>(emptySet()) }
    var loading by remember(leagueId) { mutableStateOf(false) }
    var error by remember(leagueId) { mutableStateOf<String?>(null) }

    LaunchedEffect(leagueId, roundOptions) {
        if (roundOptions.isEmpty()) {
            responses = emptyMap()
            return@LaunchedEffect
        }
        loading = true
        try {
            responses = coroutineScope {
                roundOptions
                    .associate { (phase, round) -> roundKey(phase, round) to async { Api.fetchPlayerOfRound(null, leagueId, phase, round) } }
                    .mapValues { it.value.await() }
            }
            error = null
        } catch (e: Throwable) {
            error = "Načtení hráče kola selhalo: ${e.apiMessage()}"
        }
        loading = false
    }

    if (roundOptions.isEmpty()) {
        Div { Text("Zatím nebyl odehraný žádný zápas.") }
        return
    }

    if (loading) Text("Načítám…")
    error?.let { Div { Text("Chyba: $it") } }

    // Newest round first (roundOptions is chronological).
    val rows = roundOptions.asReversed().map { (phase, round) ->
        val key = roundKey(phase, round)
        RoundRowData(key, "", phase, round, responses[key])
    }
    PlayerOfRoundTable(
        rows = rows,
        showLeagueColumn = false,
        expanded = expanded,
        onToggle = { key -> expanded = if (key in expanded) expanded - key else expanded + key },
    )
}

/**
 * Combined view across every league in the season, one table sorted by round (newest first) with a "Liga" column.
 * Fetched via player-of-round's season-batch mode -- one Edge Function call regardless of how many
 * leagues/rounds exist, instead of one call per (league, phase, round) fired in parallel.
 */
@Composable
fun PlayerOfRoundAllLeaguesSection(seasonId: String?, leagues: List<SeasonLeagueDto>) {
    var rows by remember(seasonId) { mutableStateOf<List<RoundRowData>>(emptyList()) }
    var expanded by remember(seasonId) { mutableStateOf<Set<String>>(emptySet()) }
    var loading by remember(seasonId) { mutableStateOf(false) }
    var error by remember(seasonId) { mutableStateOf<String?>(null) }
    val leagueNameById = remember(leagues) { leagues.associate { it.id to it.name } }

    LaunchedEffect(seasonId) {
        if (seasonId == null) {
            rows = emptyList()
            return@LaunchedEffect
        }
        loading = true
        try {
            rows = Api.fetchPlayerOfRoundSeasonBatch(null, seasonId)
                .map { r ->
                    val key = "${r.leagueId}:${roundKey(r.phase, r.round)}"
                    RoundRowData(key, leagueNameById[r.leagueId] ?: r.leagueName, r.phase, r.round, PlayerOfRoundResponseDto(r.isFinal, r.nominees))
                }
                // Newest round first; leagues within the same round stay alphabetical.
                .sortedWith(
                    compareByDescending<RoundRowData> { phaseOrder(it.phase) }
                        .thenByDescending { it.round }
                        .thenBy { it.leagueName }
                )
            error = null
        } catch (e: Throwable) {
            error = "Načtení hráče kola selhalo: ${e.apiMessage()}"
        }
        loading = false
    }

    if (loading) Text("Načítám…")
    error?.let { Div { Text("Chyba: $it") } }

    if (rows.isEmpty()) {
        if (!loading) Div { Text("Zatím nebyl odehraný žádný zápas.") }
        return
    }

    PlayerOfRoundTable(
        rows = rows,
        showLeagueColumn = true,
        expanded = expanded,
        onToggle = { key -> expanded = if (key in expanded) expanded - key else expanded + key },
    )
}

private const val ALL_LEAGUES = "__all__"

@Composable
fun PlayerOfRoundScreen(seasonId: String?, leagues: List<SeasonLeagueDto>) {
    var selectedLeagueId by remember { mutableStateOf(ALL_LEAGUES) }
    var matches by remember { mutableStateOf<List<MatchDto>>(emptyList()) }
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(selectedLeagueId) {
        if (selectedLeagueId == ALL_LEAGUES || selectedLeagueId.isBlank()) return@LaunchedEffect
        loading = true
        try {
            matches = Api.fetchMatches(null, selectedLeagueId)
            error = null
        } catch (e: Throwable) {
            error = "Načtení zápasů selhalo: ${e.apiMessage()}"
        }
        loading = false
    }

    Div(attrs = { classes("card") }) {
        if (leagues.isEmpty()) {
            Text("Právě není žádná aktivní sezóna.")
            return@Div
        }

        Div(attrs = { classes("actions") }) {
            Button(attrs = { if (selectedLeagueId == ALL_LEAGUES) classes("active"); onClick { selectedLeagueId = ALL_LEAGUES } }) { Text("Všechny ligy") }
            leagues.forEach { league ->
                Button(attrs = {
                    if (selectedLeagueId == league.id) classes("active")
                    onClick { selectedLeagueId = league.id }
                }) { Text(league.name) }
            }
        }

        if (selectedLeagueId == ALL_LEAGUES) {
            PlayerOfRoundAllLeaguesSection(seasonId = seasonId, leagues = leagues)
        } else {
            if (loading) Text("Načítám…")
            error?.let { Div { Text("Chyba: $it") } }

            if (!loading) {
                PlayerOfRoundSection(leagueId = selectedLeagueId, matches = matches)
            }
        }
    }
}
